import { useState, useEffect } from 'react'
import { 
  MagnifyingGlass, 
  DotsThreeVertical,
  User,
  Envelope,
  Phone,
  Calendar,
  Eye,
  PencilSimple,
  Trash,
  Download,
  Funnel,
  Crown,
  CheckCircle,
  XCircle
} from 'phosphor-react'
import { Menu } from '@headlessui/react'
import { toast } from 'react-hot-toast'
import { getConfig } from '../config/constants'

const config = getConfig()
const API_BASE_URL = config.API_URL

// Hook para autenticação
const useAuth = () => {
  const token = localStorage.getItem('admin_token')
  return { token }
}

interface Subscription {
  id: string
  userId: string
  userName: string
  userEmail: string
  plan: string
  status: 'Ativa' | 'Cancelada' | 'Pausada' | 'Vencida' | 'Pendente'
  startDate: string
  endDate: string
  amount: number
  paymentMethod: string
  lastPayment?: string
  nextPayment?: string
}

interface Pagination {
  page: number
  limit: number
  total: number
  pages: number
}

export default function Subscriptions() {
  const { token } = useAuth()
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<Subscription[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [planFilter, setPlanFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })

  // Buscar assinaturas do backend
  const fetchSubscriptions = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      })

      if (searchTerm) params.append('search', searchTerm)
      if (statusFilter !== 'all') params.append('status', statusFilter)
      if (planFilter !== 'all') params.append('plan', planFilter)

              const response = await fetch(`${API_BASE_URL}/admin/subscriptions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Erro ao buscar assinaturas')
      }

      const data = await response.json()
      setSubscriptions(data.subscriptions)
      setFilteredSubscriptions(data.subscriptions)
      setPagination(data.pagination)
    } catch (error) {
      console.error('Erro ao buscar assinaturas:', error)
      toast.error('Erro ao carregar assinaturas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSubscriptions()
  }, [pagination.page, pagination.limit, searchTerm, statusFilter, planFilter])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativa':
        return 'badge-success'
      case 'Pendente':
        return 'badge-warning'
      case 'Cancelada':
        return 'badge-error'
      case 'Vencida':
        return 'badge-error'
      case 'Pausada':
        return 'badge-info'
      default:
        return 'badge-info'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Ativa':
        return <CheckCircle className="h-4 w-4" />
      case 'Pendente':
        return <Calendar className="h-4 w-4" />
      case 'Cancelada':
        return <XCircle className="h-4 w-4" />
      case 'Vencida':
        return <XCircle className="h-4 w-4" />
      case 'Pausada':
        return <Calendar className="h-4 w-4" />
      default:
        return <Calendar className="h-4 w-4" />
    }
  }

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'Premium':
        return 'bg-purple-100 text-purple-800'
      case 'Pro':
        return 'bg-blue-100 text-blue-800'
      case 'Básico':
        return 'bg-gray-100 text-gray-800'
      case 'Gratuito':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleStatusChange = async (subscriptionId: string, newStatus: Subscription['status']) => {
    try {
      // TODO: Implementar endpoint para atualizar status da assinatura
      toast.success(`Status da assinatura atualizado para ${newStatus}`)
      // Recarregar dados após atualização
      fetchSubscriptions()
    } catch (error) {
      console.error('Erro ao atualizar status:', error)
      toast.error('Erro ao atualizar status da assinatura')
    }
  }

  const handleCancelSubscription = (subscriptionId: string) => {
    if (window.confirm('Tem certeza que deseja cancelar esta assinatura?')) {
      handleStatusChange(subscriptionId, 'Cancelada')
    }
  }

  const handleExportSubscriptions = () => {
    const csvContent = [
      ['Usuário', 'Email', 'Plano', 'Status', 'Início', 'Fim', 'Valor', 'Método de Pagamento'].join(','),
      ...filteredSubscriptions.map(sub => [
        sub.userName,
        sub.userEmail,
        sub.plan,
        sub.status,
        sub.startDate,
        sub.endDate,
        sub.amount.toFixed(2),
        sub.paymentMethod
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `assinaturas_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    
    toast.success('Relatório de assinaturas exportado com sucesso!')
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getTotalRevenue = () => {
    return subscriptions
      .filter(sub => sub.status === 'Ativa')
      .reduce((total, sub) => total + sub.amount, 0)
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assinaturas</h1>
          <p className="text-gray-600">Gerencie todas as assinaturas da plataforma</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleExportSubscriptions}
            className="btn btn-secondary btn-sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </button>
          <button className="btn btn-primary btn-sm">
            {/* <Plus className="h-4 w-4 mr-2" /> */}
            Nova Assinatura
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card card-body">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total de Assinaturas</p>
              <p className="text-2xl font-bold text-gray-900">{subscriptions.length}</p>
            </div>
            {/* <CreditCard className="h-8 w-8 text-blue-600" /> */}
          </div>
        </div>
        <div className="card card-body">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Assinaturas Ativas</p>
              <p className="text-2xl font-bold text-green-600">
                {subscriptions.filter(s => s.status === 'Ativa').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>
        <div className="card card-body">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Receita Mensal</p>
              <p className="text-2xl font-bold text-yellow-600">
                {formatCurrency(getTotalRevenue())}
              </p>
            </div>
            {/* <Money className="h-8 w-8 text-yellow-600" /> */}
          </div>
        </div>
        <div className="card card-body">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pendentes</p>
              <p className="text-2xl font-bold text-red-600">
                {subscriptions.filter(s => s.status === 'Pendente').length}
              </p>
            </div>
            {/* <Clock className="h-8 w-8 text-red-600" /> */}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="card card-body">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar assinaturas..."
                className="input pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-full sm:w-48"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos os status</option>
            <option value="Ativa">Ativa</option>
            <option value="Pendente">Pendente</option>
            <option value="Cancelada">Cancelada</option>
            <option value="Vencida">Vencida</option>
            <option value="Pausada">Pausada</option>
          </select>
          <select
            className="input w-full sm:w-48"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="all">Todos os planos</option>
            <option value="Básico">Básico</option>
            <option value="Premium">Premium</option>
            <option value="Pro">Pro</option>
            <option value="Gratuito">Gratuito</option>
          </select>
        </div>
      </div>

      {/* Tabela de Assinaturas */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th>Usuário</th>
                <th>Plano</th>
                <th>Status</th>
                <th>Valor</th>
                <th>Início</th>
                <th>Próximo Pagamento</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id}>
                  <td>
                    <div>
                      <p className="font-medium text-gray-900">{subscription.userName}</p>
                      <p className="text-sm text-gray-500">{subscription.userEmail}</p>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getPlanColor(subscription.plan)}`}>
                      {subscription.plan}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center">
                      <span className={`badge ${getStatusColor(subscription.status)} mr-2`}>
                        {getStatusIcon(subscription.status)}
                        <span className="ml-1">{subscription.status}</span>
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="font-medium text-gray-900">
                      {formatCurrency(subscription.amount)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {subscription.paymentMethod}
                    </div>
                  </td>
                  <td>
                    <div className="text-sm text-gray-500">
                      {new Date(subscription.startDate).toLocaleDateString('pt-BR')}
                    </div>
                  </td>
                  <td>
                    {subscription.nextPayment ? (
                      <div className="text-sm text-gray-500">
                        {new Date(subscription.nextPayment).toLocaleDateString('pt-BR')}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td>
                    <Menu as="div" className="relative">
                      <Menu.Button className="p-2 text-gray-400 hover:text-gray-600">
                        <DotsThreeVertical className="h-5 w-5" />
                      </Menu.Button>
                      <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                            >
                              Ver Detalhes
                            </button>
                          )}
                        </Menu.Item>
                        {subscription.status === 'Ativa' && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleStatusChange(subscription.id, 'Pausada')}
                                className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                              >
                                Pausar
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        {subscription.status === 'Pausada' && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleStatusChange(subscription.id, 'Ativa')}
                                className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-gray-700`}
                              >
                                Reativar
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        {subscription.status === 'Ativa' && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => handleCancelSubscription(subscription.id)}
                                className={`${active ? 'bg-gray-100' : ''} group flex items-center w-full px-4 py-2 text-sm text-red-700`}
                              >
                                Cancelar
                              </button>
                            )}
                          </Menu.Item>
                        )}
                      </Menu.Items>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Loading state */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="loading loading-spinner loading-md"></div>
          </div>
        )}
        
        {/* Paginação */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center py-4">
            <div className="join">
              <button
                className="join-item btn btn-sm"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                «
              </button>
              <button className="join-item btn btn-sm">
                Página {pagination.page} de {pagination.pages}
              </button>
              <button
                className="join-item btn btn-sm"
                disabled={pagination.page === pagination.pages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}